import IMask from 'imask';
import {useState, useEffect, useCallback, useMemo} from 'react';

import {IMASK_DEFINITIONS, IMASK_PATTERNS} from '../../constants/imaskPatterns';
import {updateErrorList} from '../../services/psr';
import {useChatStore} from '../../store/slices/chatStore';
import {removeMask} from '../../utils';

/**
 * PSR Item structure
 */
export interface PsrItem {
  id: string | number;
  name: string;
  idMultivanSize?: number;
  idMultivanSizeMin?: number;
  idMultivanPrefix?: string | number;
  idMultivan?: string;
}

/**
 * Props for usePsrCodeInput hook
 */
export interface UsePsrCodeInputProps {
  psrItem: PsrItem;
  psrCode?: string;
  psrCodeIsCnpj?: boolean;
  haveErrorIntoListPsr: Array<{id: string | number; error: boolean}>;
  setErrorIntoListPsr: (
    errors: Array<{id: string | number; error: boolean}>
  ) => void;
}

/**
 * Custom hook for PSR code input handling
 * Encapsulates all business logic for PSR code validation and formatting
 *
 * Features:
 * - Automatic prefix handling
 * - Length validation
 * - Error state management
 * - CNPJ formatting support
 * - Integration with chat store
 *
 * @param props - Hook configuration
 * @returns Object with state and handlers
 *
 * @example
 * ```tsx
 * const {
 *   inputCodePsr,
 *   styleEmpty,
 *   formattedPsrCode,
 *   handleChange,
 *   handleBlur
 * } = usePsrCodeInput({
 *   psrItem,
 *   psrCode,
 *   psrCodeIsCnpj,
 *   haveErrorIntoListPsr,
 *   setErrorIntoListPsr
 * });
 * ```
 */
export const usePsrCodeInput = ({
  psrItem,
  psrCode,
  psrCodeIsCnpj,
  haveErrorIntoListPsr,
  setErrorIntoListPsr
}: UsePsrCodeInputProps) => {
  const listCodePsr = useChatStore((state) => state.listCodePsr);
  const responseTypeChange = useChatStore((state) => state.responseTypeChange);
  const setChaListCode = useChatStore((state) => state.setChaListCode);

  const [inputCodePsr, setInputCodePsr] = useState<string>('');
  const [inputCodePsrError, setInputCodePsrError] = useState<boolean>(true);
  const [styleEmpty, setStyleEmpty] = useState<string>('');

  /**
   * Updates the error list for PSR validation
   */
  const updatePsrErrorList = useCallback(
    (statusError: boolean): void => {
      const newListErrors = updateErrorList(
        haveErrorIntoListPsr,
        psrItem.id,
        statusError
      );
      setInputCodePsrError(statusError);
      setErrorIntoListPsr(newListErrors);
    },
    [haveErrorIntoListPsr, psrItem.id, setErrorIntoListPsr]
  );

  /**
   * Tests if the Multivan ID meets minimum length requirement
   */
  const testMinIdMultivan = useCallback(
    (value: string): void => {
      const sizeMin = psrItem.idMultivanSizeMin;
      const idMultivan = removeMask(value);

      if (sizeMin && idMultivan.length < sizeMin) {
        updatePsrErrorList(true);
      } else {
        updatePsrErrorList(false);
      }
    },
    [psrItem.idMultivanSizeMin, updatePsrErrorList]
  );

  /**
   * Updates the Multivan ID in the chat store list
   */
  const updateIdMultivanList = useCallback(
    (psrIdMultivan: string): void => {
      const newList = listCodePsr.map((inputPsr) => {
        if (inputPsr.id === psrItem.id) {
          return {
            ...inputPsr,
            idMultivan: psrIdMultivan
          };
        }
        return inputPsr;
      });

      setChaListCode(newList);
    },
    [listCodePsr, psrItem.id, setChaListCode]
  );

  /**
   * Handles code changes with prefix and size validation
   */
  const handleChange = useCallback(
    (value: string): void => {
      let size = psrItem.idMultivanSize;

      if (psrItem.idMultivanPrefix) {
        size = size ? size + 1 : undefined;
        const prefix = psrItem.idMultivanPrefix.toString();

        if (value.substring(0, prefix.length + 1) !== prefix + '-') {
          value = prefix + '-' + removeMask(value);
        } else {
          const numb2 = value.slice(prefix.length + 1, value.length);
          value = prefix + '-' + removeMask(numb2);
        }
      } else {
        value = removeMask(value);
      }

      if (size) {
        value = value.slice(0, size);
        setInputCodePsr(value);
      }

      testMinIdMultivan(value);
      updateIdMultivanList(value);
    },
    [
      psrItem.idMultivanSize,
      psrItem.idMultivanPrefix,
      testMinIdMultivan,
      updateIdMultivanList
    ]
  );

  /**
   * Validates and updates the code on blur
   */
  const handleBlur = useCallback((): void => {
    testMinIdMultivan(inputCodePsr);
    updateIdMultivanList(inputCodePsr);
  }, [inputCodePsr, testMinIdMultivan, updateIdMultivanList]);

  /**
   * Updates border style based on input state
   */
  useEffect(() => {
    if (inputCodePsr === '') {
      setStyleEmpty('content-code__psr--border-blue');
    } else if (inputCodePsrError) {
      setStyleEmpty('content-code__psr--border-red');
    } else {
      setStyleEmpty('');
    }
  }, [inputCodePsr, inputCodePsrError]);

  /**
   * Disables response type on mount
   */
  useEffect(() => {
    responseTypeChange('disabled');
  }, [responseTypeChange]);

  /**
   * Syncs input with prop changes
   */
  useEffect(() => {
    setInputCodePsr(psrCode ?? '');
  }, [psrCode]);

  /**
   * Formats CNPJ when psrCodeIsCnpj is true
   */
  const formattedPsrCode = useMemo(() => {
    if (psrCodeIsCnpj && inputCodePsr) {
      const mask = IMask.createMask({
        mask: IMASK_PATTERNS.CNPJ,
        definitions: IMASK_DEFINITIONS.CNPJ,
        lazy: false
      });
      mask.resolve(inputCodePsr);
      return mask.value;
    }
    return inputCodePsr;
  }, [inputCodePsr, psrCodeIsCnpj]);

  return {
    inputCodePsr,
    styleEmpty,
    formattedPsrCode,
    handleChange,
    handleBlur
  };
};
